import { Fragment, type ReactNode, useCallback, useEffect, useMemo } from 'react'

import { IntlProvider } from 'react-intl'

import {
	MediaQueryProvider,
	NoOpErrorBoundary,
	RouterProvider,
	renderDefaultSnackbar,
	SnackbarProvider,
	ThemeProvider,
	useStore,
} from '@subaio/components'
import { type Api, type EventLogger, logError, type Page, type Router } from '@subaio/utils'
import { ContainerStatus, initializeContainer } from '@subaio/webapp/src/containers'

import type { AbnTracking, PageviewOptions } from 'lib/abnTracking'
import type { Container, OnLogEvent } from 'lib/container'
import { getContainer } from 'lib/getContainer'
import { intlFormats } from 'lib/intlFormats'
import type { Store } from 'lib/store'

import { GlobalStyle, getTheme } from 'styles'
import { queries } from 'styles/responsiveQuery'

import { Error } from 'components/Error'
import { ModalProvider } from 'components/Modal'

type Props = {
	clientApi: Api
	store: Store
	router: Router
	eventLogger?: EventLogger
	abnTracking: AbnTracking
	containers: Record<string, Container>
}

export const App = ({ clientApi, store, router, eventLogger, containers, abnTracking }: Props) => {
	const { page, status, data, userAuth, translation } = useStore(store)

	const theme = useMemo(() => getTheme(), [])

	const container = getContainer(containers, page, userAuth)

	const setData = useCallback(
		(update: Record<string, any>) => {
			const { data, page: currentPage } = store.getState()
			// Abort state updates if page has changed
			if (page !== currentPage) return
			store.set({ data: { ...data, ...update } })
		},
		[store, page],
	)

	const updatePage = useCallback(
		(page: Page) => {
			store.set({ page, status: ContainerStatus.Initializing, data: {} })
		},
		[store],
	)

	const onLogEvent = useCallback<OnLogEvent>(
		(identifier, data?) => {
			eventLogger?.log('event', identifier, data)
		},
		[eventLogger],
	)

	const onLogPageView = useCallback(
		(form: PageviewOptions['form']) => {
			abnTracking.logPageview({ locale: translation!.locale, pageName: page.name, form })
		},
		[abnTracking, translation, page],
	)

	const title = useMemo(() => {
		if (!container?.title) return undefined

		if (typeof container.title !== 'function') return container.title

		if (status !== ContainerStatus.Running) return undefined

		return container.title({ ...page.params, ...data }) as ReactNode
	}, [container, data, page, status])

	useEffect(() => {
		if (typeof title !== 'string') return

		const original = document.title
		document.title = `${title} • ${original}`

		return () => {
			document.title = original
		}
	}, [title])

	useEffect(() => {
		if (status !== ContainerStatus.Initializing || userAuth.status !== 'authenticated') return

		initializeContainer(store, container, { query: page.query, updatePage, router, clientApi }, (path) =>
			clientApi.get(path),
		).catch(() => {})
	}, [store, container, status, updatePage, page, router, clientApi, userAuth])

	useEffect(() => {
		if (!translation) return

		const defaultValue = document.documentElement.lang

		document.documentElement.lang = translation.locale

		return () => {
			document.documentElement.lang = defaultValue
		}
	}, [translation])

	const props = {
		...page.params,
		store,
		clientApi,
		query: page.query,
		setData,
		onLogEvent,
		onLogPageView,
	}

	return (
		<ThemeProvider theme={theme}>
			<GlobalStyle />
			<IntlProvider
				locale={translation?.locale ?? 'en-NL'}
				messages={translation?.messages}
				defaultLocale='en-NL'
				formats={intlFormats}
				defaultFormats={intlFormats}
			>
				<RouterProvider router={router} page={page} onPageChange={updatePage}>
					<MediaQueryProvider queries={queries}>
						<ModalProvider mode='fixed'>
							<SnackbarProvider render={renderDefaultSnackbar}>
								<NoOpErrorBoundary
									onError={(error, info) => {
										logError(error, info)

										store.set({ status: ContainerStatus.Errored })
									}}
								>
									<Fragment key={page.name}>
										{/* TODO: make better system for loading during translation loading */}
										{status === ContainerStatus.Running && translation && <container.Running {...data} {...props} />}
										{status === ContainerStatus.Loading && <container.Loading {...data} {...props} />}
										{status === ContainerStatus.Initializing && (
											<container.Loading {...container.defaults} {...props} />
										)}
										{status === ContainerStatus.Errored && <Error />}
									</Fragment>
								</NoOpErrorBoundary>
							</SnackbarProvider>
						</ModalProvider>
					</MediaQueryProvider>
				</RouterProvider>
			</IntlProvider>
		</ThemeProvider>
	)
}
